/* חיובים קבועים מול מה שבאמת ירד.
   לכל חיוב קבוע ולכל מועד שלו מחפשים את העסקה האמיתית: אותו מקור (כרטיס או עו"ש), שם דומה,
   תאריך קרוב (עד 12 יום לכל כיוון) וסכום סביר (גם כשהוא משתנה מחודש לחודש). מצבי מועד:
   ‣ paid    ירד: נמצאה עסקה. היא כבר ביתרה או בסך האשראי, ולכן לא נספרת שוב.
   ‣ due     צפוי: התאריך עוד לא הגיע.
   ‣ late    טרם נקלט: התאריך עבר, העסקה עוד לא בסריקה. ממשיכים לספור אותו.
   ‣ missing לא נמצא: הסריקה כבר מכסה כמה ימים אחרי התאריך ואין עסקה. נספר (זהירות), ומבקשים לבחור.
   ‣ skip    סומן שלא ירד החודש.
   ‣ old     ישן, כבר בתוך היתרה או בחיוב כרטיס שירד.
   הסכום הצפוי של חיוב משתנה הוא חציון שלושת החיובים האחרונים שזוהו. */
import { S, memo } from './state.js';
import { mNorm } from '../lib/text.js';
import { fISO, fDim, todayISO, iso10, median, absAmt, wD, fMoney, MONTHS } from '../lib/format.js';
import { getTxSrc, isExcluded, CARD_RX } from './tx.js';
import { curKey, periodStart } from './periods.js';
import { cardTemplate, cardKeyOf, cardCurKey } from './card.js';
import { scanCoverage } from './coverage.js';

// מילים שחוזרות בתיאורי בנק וכרטיס ולא מזהות את בית העסק
const REC_STOP = new Set(['בע', 'מ', 'בעמ', 'ltd', 'inc', 'com', 'www', 'co', 'il', 'the', 'חברת', 'חברה', 'ישראל', 'לישראל', 'הסכ', 'בעם',
  'הוק', 'הו', 'ק', 'הרשאה', 'חיוב', 'תשלום', 'העברה', 'הוראת', 'קבע', 'לחשבון', 'בנק']);

export function recCore(s) {
  return mNorm(s).trim().split(' ').filter((t) => t.length >= 2 && !REC_STOP.has(t))
    .map((t) => (t.length >= 5 && t.charAt(0) === 'ה' ? t.slice(1) : t)); // "החשמל" = "חשמל"
}
export function recSrcOf(r) {
  return (r.source || 'cash') === 'credit' ? 'credit' : 'cash';
}
export function recIsCardTpl(r) {
  return cardTemplate() === r;
}
export function recEvery(r) {
  const n = Number(r && r.every_months) || 1;
  return n < 1 ? 1 : n;
}
function recStartMonth(r) {
  return Number(r.start_month) || (r.created_at ? new Date(r.created_at).getMonth() + 1 : 1);
}
/** חיוב שלא חוזר כל חודש יורד רק בחודשים שמתאימים לתדירות שלו */
export function recFires(r, d) {
  const n = recEvery(r);
  if (n <= 1) return true;
  return ((((d.getMonth() + 1 - recStartMonth(r)) % n) + n) % n) === 0;
}
export function recFreqLabel(r) {
  const n = recEvery(r);
  if (n <= 1) return '';
  const names = { 2: 'כל חודשיים', 3: 'כל 3 חודשים', 6: 'כל חצי שנה', 12: 'פעם בשנה' };
  return (names[n] || 'כל ' + n + ' חודשים') + (n === 12 ? ' (' + MONTHS[recStartMonth(r) - 1] + ')' : '');
}

/** כמה השם של העסקה דומה לחיוב הקבוע: 1 זהה או שויך ידנית, 0.9 תחילית, 0.8 אותה מילה ראשונה, 0 לא קשור */
export function recNameScore(r, t, rn, tn, rc, tc) {
  const M = S.set.recMatch;
  const lk = M.link[t.id];
  if (lk != null) return String(lk) === String(r.id) ? 1 : 0;
  if (rn == null) rn = mNorm(r.description).trim();
  if (tn == null) tn = mNorm(t.description).trim();
  if (!rn || !tn) return 0;
  if (rn === tn || t.description === r.description) return 1;
  const al = M.alias[r.id] || [];
  for (const a of al) if (tn === a || (a.length >= 4 && tn.indexOf(a) === 0)) return 1;
  if (rn.length >= 6 && tn.indexOf(rn) === 0) return 0.9;
  if (tn.length >= 6 && rn.indexOf(tn) === 0) return 0.9;
  rc = rc || recCore(r.description);
  tc = tc || recCore(t.description);
  // אותה מילה ראשונה, אלא אם המילה השנייה סותרת ("הראל חיים" מול "הראל בריאות")
  if (rc[0] && tc[0] && rc[0] === tc[0] && rc[0].length >= 4 &&
    !(rc[1] && tc[1] && rc[1].length >= 3 && tc[1].length >= 3 && rc[1] !== tc[1])) return 0.8;
  // מילה משמעותית משותפת ("חשמל" ב"הו"ק חברת החשמל"): התאמה חלשה, ולכן הסכום חייב להיות קרוב
  for (const w of rc) if (w.length >= 4 && tc.indexOf(w) >= 0) return 0.7;
  return 0;
}

export function recOccDates(r, fromD, toD) {
  const out = [], y = fromD.getFullYear(), m = fromD.getMonth();
  for (let i = 0; i < 16; i++) {
    let mm = m + i;
    const yy = y + Math.floor(mm / 12);
    mm = ((mm % 12) + 12) % 12;
    const day = Math.min(Number(r.day_of_month) || 1, fDim(yy, mm));
    const d = new Date(yy, mm, day, 12);
    if (d > toD) break;
    if (d < fromD || !recFires(r, d)) continue;
    out.push(d);
  }
  return out;
}

/** ההתאמה המלאה. נשמרת עד שהנתונים משתנים */
export function recMatchAll() {
  return memo('recMatchAll', computeRecMatch);
}
function computeRecMatch() {
  const today = todayISO();
  const t0 = new Date(today + 'T12:00:00');
  const from = new Date(t0.getTime() - 130 * 864e5), to = new Date(t0.getTime() + 45 * 864e5);
  const fromISO = fISO(new Date(from.getTime() - 21 * 864e5));
  // שמות מנורמלים מחושבים פעם אחת, כי ההשוואה רצה על כל זוג
  const txs = [];
  S.tx.forEach((t) => {
    const ds = iso10(t.date);
    if (!ds || ds < fromISO || (t.type !== 'expense' && t.type !== 'income')) return;
    txs.push({ t, ms: new Date(ds + 'T12:00:00').getTime(), src: getTxSrc(t), tn: mNorm(t.description).trim(), tc: recCore(t.description), a: absAmt(t) });
  });
  const link = S.set.recMatch.link;
  const occ = [], pairs = [];
  S.rec.forEach((r) => {
    if (recIsCardTpl(r)) return;
    const src = recSrcOf(r), ra = Math.abs(Number(r.amount) || 0), win = recEvery(r) > 1 ? 20 : 12;
    const rn = mNorm(r.description).trim(), rc = recCore(r.description);
    recOccDates(r, from, to).forEach((d) => {
      const o = { r, d, iso: fISO(d), ym: fISO(d).slice(0, 7), key: r.id + '|' + fISO(d) };
      occ.push(o);
      txs.forEach((x) => {
        const t = x.t;
        if (t.type !== r.type || x.src !== src) return;
        const dd = Math.abs(x.ms - d.getTime()) / 864e5;
        if (dd > win) return;
        let ns = recNameScore(r, t, rn, x.tn, rc, x.tc);
        const ta = x.a;
        /* בלי שם דומה: הכנסה בסכום קרוב (משכורת מגיעה לפעמים בשם המעסיק או הקופה), או הוצאה כמעט זהה בסכום ובתאריך.
           בלי זה, משכורת שנכנסה בשם אחר נספרת פעמיים: פעם ביתרה ופעם כ"טרם נקלט" */
        if (!ns && ra > 0 && link[t.id] == null) {
          if (r.type === 'income' && dd <= 6 && ta / ra >= 0.85 && ta / ra <= 1.15) ns = 0.6;
          else if (r.type !== 'income' && dd <= 3 && ta / ra >= 0.98 && ta / ra <= 1.02) ns = 0.55;
        }
        if (!ns) return;
        if (ra > 0 && ns < 1 && (ta / ra > 2 || ra / ta > 2)) return; // סכום רחוק מדי
        if (ra > 0 && ns < 0.8 && (ta / ra > 1.5 || ra / ta > 1.5)) return;
        if (ra > 0 && ns >= 1 && link[t.id] == null && (ta / ra > 3 || ra / ta > 3)) return;
        const ad = ra > 0 ? Math.abs(ta - ra) / ra : 0;
        pairs.push({ o, t, s: ns * 100 - dd * 2 - Math.min(ad, 1) * 25 });
      });
    });
  });
  pairs.sort((a, b) => b.s - a.s);
  const byOcc = {}, byTx = {};
  pairs.forEach((p) => {
    if (byOcc[p.o.key] || byTx[p.t.id]) return;
    byOcc[p.o.key] = p.t;
    byTx[p.t.id] = p.o.r.id;
  });
  // הסכום הצפוי: חציון שלושת החיובים האחרונים שזוהו (לפני היום)
  const est = {}, hist = {};
  occ.forEach((o) => {
    const t = byOcc[o.key];
    if (t && o.iso <= today) (hist[o.r.id] = hist[o.r.id] || []).push({ d: o.iso, a: absAmt(t), t });
  });
  S.rec.forEach((r) => {
    const h = (hist[r.id] || []).sort((a, b) => (a.d < b.d ? -1 : 1)).slice(-3);
    est[r.id] = h.length >= 2 ? Math.round(median(h.map((x) => x.a)) * 100) / 100 : Math.abs(Number(r.amount) || 0);
  });
  // מועד אחרון שהסריקה מכסה, לכל מקור
  const cov = { cash: '', credit: '' };
  scanCoverage().forEach((c) => { if (!cov[c.src] || c.date > cov[c.src]) cov[c.src] = c.date; });
  const ck = cardCurKey(), anchor = S.set.balDate || '', eod = S.set.balEod;
  occ.forEach((o) => {
    const src = recSrcOf(o.r);
    o.t = byOcc[o.key] || null;
    o.amount = o.t ? absAmt(o.t) : est[o.r.id];
    if (o.t) o.st = 'paid';
    else if ((S.set.recMatch.skip[o.r.id] || []).indexOf(o.ym) >= 0) o.st = 'skip';
    else if (o.iso > today) o.st = 'due';
    else if (src === 'credit' && cardKeyOf(o.d) < ck) o.st = 'old';                      // בחיוב כרטיס שכבר ירד
    else if (src === 'cash' && anchor && (eod ? o.iso <= anchor : o.iso < anchor)) o.st = 'old'; // לפני היתרה שהזנת
    else {
      // הסריקה כבר מכסה את המועד ואין עסקה: "לא נמצא". אחרי 35 יום כנראה באמת לא ירד, ולכן ישן.
      // בלי סריקה שמכסה את המועד הוא כנראה קרה, ונשאר "טרם נקלט" גם אחרי 35 יום, עד שיש נתונים
      const c = cov[src], covered = c && c >= fISO(new Date(o.d.getTime() + 4 * 864e5));
      o.st = !covered ? 'late' : (t0 - o.d) / 864e5 > 35 ? 'old' : 'missing';
    }
  });
  const map = {};
  occ.forEach((o) => (map[o.key] = o));
  return { occ, byOcc, byTx, est, hist, cov, map };
}

export function recOcc(r, iso) {
  return recMatchAll().map[r.id + '|' + iso] || null;
}
export function recEst(r) {
  const M = recMatchAll();
  return M.est[r.id] != null ? M.est[r.id] : Math.abs(Number(r.amount) || 0);
}
/** מועדים שעברו ועדיין לא נקלטו: נכנסים לתחזית מחר (עו"ש) או לחיוב הכרטיס הקרוב (אשראי) */
export function recOpen(src) {
  return recMatchAll().occ.filter((o) => (o.st === 'late' || o.st === 'missing') && recSrcOf(o.r) === src);
}
/** המועד הרלוונטי של חיוב קבוע עכשיו: האחרון שעבר בחלון, או הקרוב */
export function recNow(r) {
  const today = todayISO();
  let best = null;
  recMatchAll().occ.forEach((o) => {
    if (o.r !== r || o.st === 'old') return;
    if (o.st === 'late' || o.st === 'missing') {
      if (!best || best.st === 'paid' || best.st === 'due' || o.iso < best.iso) best = o;
      return;
    }
    if (o.st === 'paid' && o.iso <= today) {
      if (!best || (best.st === 'paid' && o.iso > best.iso) || best.st === 'due') best = o;
      return;
    }
    if (o.st === 'paid' || o.st === 'due' || o.st === 'skip') { if (!best) best = o; }
  });
  return best;
}
export function recStatusTxt(o) {
  if (!o) return '';
  const d = wD(o.d);
  if (o.st === 'paid') return '✓ ירד ' + wD(o.t.date) + ' · ' + fMoney(o.amount);
  if (o.st === 'due') return 'צפוי ' + d + ' · ~' + fMoney(o.amount);
  if (o.st === 'late') return '⏳ טרם נקלט (צפוי היה ' + d + ')';
  if (o.st === 'missing') return '⚠️ לא נמצא בסריקה (' + d + ')';
  if (o.st === 'skip') return 'דולג החודש';
  return '';
}

/** עסקאות שיכולות להיות החיוב: אותו מקור וסוג, עד 15 יום מהמועד, ועוד לא משויכות לחיוב קבוע אחר */
export function recCandidates(o) {
  const M = recMatchAll(), src = recSrcOf(o.r), ra = Math.abs(Number(o.r.amount) || 0);
  return S.tx.filter((t) => {
    if (t.type !== o.r.type || getTxSrc(t) !== src || M.byTx[t.id] != null) return false;
    if (isExcluded(t) || (src === 'cash' && CARD_RX.test(t.description || ''))) return false;
    return Math.abs((new Date(iso10(t.date) + 'T12:00:00') - o.d) / 864e5) <= 15;
  }).map((t) => {
    const ta = absAmt(t);
    return { t, s: recNameScore(o.r, t) * 100 + (ra ? Math.max(0, 40 - (Math.abs(ta - ra) / ra) * 60) : 0) };
  }).sort((a, b) => b.s - a.s).slice(0, 8).map((x) => x.t);
}

/** המועד של חיוב קבוע בכרטיס שנופל במחזור החיוב הנוכחי */
export function recCardCur(r) {
  const k = cardCurKey();
  let best = null;
  recMatchAll().occ.forEach((o) => {
    if (o.r === r && o.st !== 'old' && cardKeyOf(o.d) === k && (!best || o.iso > best.iso)) best = o;
  });
  return best && best.st !== 'skip' ? best : null;
}

/** זיהוי חיוב קבוע בשורה של סריקה (לפני שנשמרה): לפי שם, סכום סביר ומועד קרוב */
export function recForScanItem(it, src) {
  if (!it || !S.rec.length) return null;
  const ds = iso10(it.date);
  if (!/^\d{4}-\d\d-\d\d$/.test(ds)) return null;
  const d = new Date(ds + 'T12:00:00'), ta = Math.abs(Number(it.amount) || 0);
  const fake = { id: 'scan', description: it.description || it.name || '', type: it.type || 'expense' };
  let best = null, bs = 0;
  S.rec.forEach((r) => {
    if (recIsCardTpl(r) || recSrcOf(r) !== (src === 'credit' ? 'credit' : 'cash') || r.type !== fake.type) return;
    const ns = recNameScore(r, fake);
    if (!ns) return;
    const ra = Math.abs(Number(r.amount) || 0);
    if (ra > 0 && ta > 0 && (ta / ra > 2.5 || ra / ta > 2.5)) return;
    const occ = recOccDates(r, new Date(d.getTime() - 25 * 864e5), new Date(d.getTime() + 25 * 864e5));
    const dd = occ.reduce((m, x) => Math.min(m, Math.abs(x - d) / 864e5), 99);
    if (dd > (recEvery(r) > 1 ? 20 : 12)) return;
    const s = ns * 100 - dd * 2 - (ra ? Math.min(1, Math.abs(ta - ra) / ra) * 25 : 0);
    if (s > bs) { bs = s; best = r; }
  });
  return best;
}

/** חיוב נחשב "קבוע" אם השם שלו תואם חיוב קבוע מוגדר */
export function fixedMatcher() {
  const recN = S.rec.filter((x) => x.type === 'expense').map((x) => mNorm(x.description)).filter((x) => x.length >= 4);
  return (t) => {
    const n = mNorm(t.description || '');
    return recN.some((x) => n === x || (n.length >= 6 && (n.indexOf(x) === 0 || x.indexOf(n) === 0)));
  };
}
export function fixedNameSet() {
  const s = {};
  S.rec.forEach((r) => { const k = mNorm(r.description); if (k.length >= 3) s[k] = 1; });
  return s;
}
export function isFixedCharge(t, set) {
  if (!t || !t.description) return false;
  const k = mNorm(t.description);
  if (k.length < 3) return false;
  if (set[k]) return true;
  // תיאורי בנק נושאים מספרי אסמכתא, ולכן גם תחילית נחשבת
  return Object.keys(set).some((key) => key.length >= 6 && (k.indexOf(key) === 0 || key.indexOf(k) === 0));
}

/** תבניות כפולות: אותו סכום וסוג, ושם זהה או יום קרוב. נוצר כשמוסיפים ידנית משהו שכבר זוהה */
export function findDupRec() {
  const byAmt = {};
  S.rec.forEach((r) => {
    const k = Math.round(Math.abs(Number(r.amount) || 0)) + '|' + (r.type || 'expense');
    (byAmt[k] = byAmt[k] || []).push(r);
  });
  const out = [];
  Object.keys(byAmt).forEach((k) => {
    const g = byAmt[k].slice();
    if (g.length < 2) return;
    const amt = Math.round(Math.abs(Number(g[0].amount) || 0));
    g.sort((a, b) => (Number(a.day_of_month) || 1) - (Number(b.day_of_month) || 1));
    let cluster = [g[0]];
    for (let i = 1; i < g.length; i++) {
      const prev = cluster[cluster.length - 1];
      const sameName = mNorm(g[i].description) === mNorm(prev.description);
      const closeDay = Math.abs((Number(g[i].day_of_month) || 1) - (Number(prev.day_of_month) || 1)) <= 3;
      // סכום קטן יכול לחזור באמת פעמיים בחודש, ולכן נדרש שם זהה
      if (sameName || (closeDay && amt >= 200)) cluster.push(g[i]);
      else { if (cluster.length > 1) out.push(cluster); cluster = [g[i]]; }
    }
    if (cluster.length > 1) out.push(cluster);
  });
  return out;
}

/** חיוב קבוע שמתאים לעסקה הזו בדיוק (לעריכת עסקה) */
export function findRecFor(t) {
  if (!t || !t.description) return null;
  return S.rec.find((r) => r.description === t.description && (r.type || 'expense') === (t.type || 'expense') &&
    Math.abs(Number(r.amount) - Math.abs(Number(t.amount))) < 0.5) || null;
}

/** חיובים שחוזרים בקביעות (3 חודשים לפחות, סכום יציב) ולא מוגדרים כחיוב קבוע */
export function findUndeclaredSubs() {
  const fNorm = (s) => String(s || '').toLowerCase().replace(/[0-9]/g, ' ').replace(/[^֐-׿a-z ]/g, ' ').replace(/\s+/g, ' ').trim();
  const groups = {};
  S.tx.forEach((t) => {
    if (t.type !== 'expense' || !t.date || isExcluded(t)) return;
    const key = fNorm(t.description);
    if (key.length < 3) return;
    (groups[key] = groups[key] || []).push(t);
  });
  const known = {};
  S.rec.forEach((r) => (known[fNorm(r.description)] = true));
  const found = [];
  Object.keys(groups).forEach((key) => {
    if (known[key]) return;
    const txs = groups[key], months = {}, days = {}, amounts = [];
    txs.forEach((t) => {
      const ds = iso10(t.date);
      months[ds.slice(0, 7)] = true;
      const d = parseInt(ds.slice(8, 10), 10);
      days[d] = (days[d] || 0) + 1;
      amounts.push(absAmt(t));
    });
    const mCount = Object.keys(months).length;
    if (mCount < 3) return;
    // חיוב שיורד בכרטיס כבר נמצא בתוך חיוב הכרטיס, ולכן לא חסר בתחזית
    if (txs.filter((t) => getTxSrc(t) === 'credit').length * 2 >= txs.length) return;
    const mn = Math.min(...amounts), mx = Math.max(...amounts);
    if (mn <= 0 || mx / mn > 1.25) return;
    const avg = amounts.reduce((a, b) => a + b, 0) / amounts.length;
    if (avg < 15) return;
    let bestDay = 1, bestN = 0;
    Object.keys(days).forEach((d) => { if (days[d] > bestN) { bestN = days[d]; bestDay = parseInt(d, 10); } });
    const last = txs[txs.length - 1];
    found.push({ name: last.description || key, cat: last.cat, amount: Math.round(avg), months: mCount, day: bestDay, yearly: Math.round(avg * 12), source: 'cash' });
  });
  return found.sort((a, b) => b.yearly - a.yearly).slice(0, 8);
}

/** תחילת התקופה הנוכחית, לשימוש מודולים אחרים */
export function curPeriodStartISO() {
  return fISO(periodStart(curKey()));
}
